import { Container } from "pixi.js";
import { Persistence } from "../config/constants";
import { BaseEntity } from "../core/entity/BaseEntity";
import { Entity } from "../core/entity/Entity";
import { on } from "../core/entity/handler";
import { isNight } from "../game/time";
import { tutorialTargets } from "../shell/hud/tutorial/tutorialTargets";
import { MachineEntity } from "../sim/entities/MachineEntity";
import { MaterialPileEntity } from "../sim/entities/MaterialPileEntity";
import { Player } from "../sim/entities/Player";
import { StandEntity } from "../sim/entities/StandEntity";
import { TruckEntity } from "../sim/entities/TruckEntity";
import { Broom } from "../sim/singletons/Broom";
import { projectTutorialFacts } from "../sim/projection";
import { Clock } from "../sim/singletons/Clock";
import { ShellStore } from "../shell/ShellStore";
import {
  TUTORIAL_HIGHLIGHT_FILTERS,
  viewHighlightRoot,
} from "./targetHighlight";

/**
 * What the guided opening points at in the world (see game/tutorial.ts) —
 * the old shell's coach outlines (`targetHighlight.ts`'s tutorial
 * filters, threaded through ShopView's sprite props) applied by an
 * entity instead, the way TargetHighlightView applies the white rim.
 *
 * Deliberately a different color from the white targeting rim: white
 * means "the keys act on this, here, now", and orange means "go to this
 * next" — a thing you usually aren't standing at yet. When both apply
 * the white rim wins, because by then the tutorial's arrow has done its
 * job: this entity renders after TargetHighlightView (added later in
 * engine-main) and skips anything already dressed this frame.
 *
 * The homeward nudge rides along exactly as it did in ShopView: at
 * close the top bar's day dial pulses and its tooltip points home (see
 * NavBar's DayClock), and the truck wears the same coach outline so the
 * warning and the world agree.
 */
export class TutorialHighlightView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private dressed: Container[] = [];
  /** Where the coach points right now, recomputed only when the world
   * moved (the ShellStore's version — everything this reads is covered
   * by an event or the signature). The dress pass itself still runs
   * every frame: the white targeting rim moves per frame, and orange
   * yields to it container by container. */
  private targets: Container[] = [];
  private computedVersion = -1;

  @on("render")
  onRender() {
    // Undress last frame's targets — but only where the orange is still
    // ours, so a white rim that took the container over stays put.
    for (const container of this.dressed) {
      if (container.filters === TUTORIAL_HIGHLIGHT_FILTERS) {
        container.filters = [];
      }
    }
    this.dressed = [];

    const game = this.game;
    if (!game.entities.tryGetSingleton(Player)) return;
    const version =
      game.entities.tryGetSingleton(ShellStore)?.version ??
      this.computedVersion + 1;
    if (version !== this.computedVersion) {
      this.computedVersion = version;
      this.targets = this.computeTargets();
    }
    for (const container of this.targets) {
      this.dress(container);
    }
  }

  /** Everything the coach is pointing at, as the containers to rim. */
  private computeTargets(): Container[] {
    const game = this.game;
    const gs = projectTutorialFacts(game);
    if (gs.player.away) return [];
    const targets: Container[] = [];
    const collect = (container: Container | null) => {
      if (container) targets.push(container);
    };

    const coach = tutorialTargets(gs);

    // At close the day dial's warning points home (see NavBar's DayClock),
    // and the truck wears the same coach outline so the two agree.
    const homewardNudge =
      isNight(game.entities.getSingleton(Clock)) && !gs.player.away
        ? "truck"
        : null;

    // The stations the coach is sending the player to.
    for (const machine of game.entities.byConstructor(MachineEntity)) {
      if (coach.machineTypeIds.has(machine.state.machineTypeId)) {
        collect(viewHighlightRoot(machine));
      }
    }

    // The truck: the tutorial only ever points at the cab, so the whole
    // body wears the rim (the old TruckSprite's "truck" treatment; no
    // step targets the bed).
    if (coach.truck !== null || homewardNudge !== null) {
      const truck = game.entities.tryGetSingleton(TruckEntity);
      if (truck) collect(viewHighlightRoot(truck));
    }

    if (coach.stand) {
      const stand = game.entities.tryGetSingleton(StandEntity);
      if (stand) collect(viewHighlightRoot(stand));
    }

    if (coach.broom) {
      const broom = game.entities.tryGetSingleton(Broom);
      if (broom) collect(viewHighlightRoot(broom));
    }

    if (coach.matchesPile) {
      for (const pile of game.entities.byConstructor(MaterialPileEntity)) {
        if (coach.matchesPile(pile.material)) {
          collect(viewHighlightRoot(pile));
        }
      }
    }
    return targets;
  }

  /** Rim a container in the coach's orange — unless the white targeting
   * rim already dressed it this frame (white wins), or it was torn down
   * since the targets were computed. */
  private dress(container: Container): void {
    if (container.destroyed) return;
    const existing = container.filters;
    if (existing && (!Array.isArray(existing) || existing.length > 0)) return;
    container.filters = TUTORIAL_HIGHLIGHT_FILTERS;
    this.dressed.push(container);
  }
}
